import Vue from 'vue'
import VueI18n from 'vue-i18n'

Vue.use(VueI18n)

// Create a global event bus for language changes
export const i18nEventBus = new Vue();

function loadLocaleMessages() {
  const locales = require.context('./locales', true, /[A-Za-z0-9-_,\s]+\.json$/i);
  const messages = {};
  locales.keys().forEach(key => {
    const matched = key.match(/([A-Za-z0-9-_]+)\./i);
    if (matched && matched.length > 1) {
      const locale = matched[1];
      messages[locale] = locales(key);
      
      // Log the structure for debugging
      //eslint-disable-next-line no-console
      console.log(`Loaded ${locale} locale with structure:`, 
        Object.keys(messages[locale]).length + ' top-level keys');
    }
  })
  return messages;
}

// Get stored language or detect browser language
function getStartingLocale() {
  // First check localStorage
  const storedLocale = localStorage.getItem('user-locale');
  if (storedLocale) {
    return storedLocale;
  }
  
  // Then check browser language
  const browserLocale = navigator.language.split('-')[0];
  const supportedLocales = ['en', 'es', 'ko', 'th', 'pt', 'zh', 'zh-Hant', 'vi', 'hi'];
  
  // Return matched locale or fallback
  const locale = supportedLocales.includes(browserLocale) ? browserLocale : 'en';
  return locale;
}

// Add performance optimization through caching
const translationCache = new Map();

const i18n = new VueI18n({
  locale: getStartingLocale(),
  fallbackLocale: process.env.VUE_APP_I18N_FALLBACK_LOCALE || 'en',
  messages: loadLocaleMessages(),
  silentTranslationWarn: true, // Suppress warnings for missing translations
  missing: (locale, key) => {
    // Log missing translations in development
    if (process.env.NODE_ENV !== 'production') {
      //eslint-disable-next-line no-console
      console.warn(`[i18n] Missing translation: ${key} in ${locale}`);
    }
    return key;
  }
});

// Original Vue I18n $t is already optimized, but we can add a thin caching layer
const originalT = i18n.t.bind(i18n);
i18n.t = function(key, values) {
  // Create a cache key based on the translation key, locale and values
  const cacheKey = `${i18n.locale}:${key}:${JSON.stringify(values || {})}`;
  
  // Check if we have a cached result
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey);
  }
  
  // Get the translation
  const result = originalT(key, values);
  
  // Cache the result (limit cache size to prevent memory issues)
  if (translationCache.size > 1000) {
    // Clear oldest cache entries if it gets too large
    const firstKey = translationCache.keys().next().value;
    translationCache.delete(firstKey);
  }
  translationCache.set(cacheKey, result);
  
  return result;
};

// Expose a method to change the language
export const setLocale = (locale) => {
  // Check if the locale files are loaded
  if (!i18n.messages[locale]) {
    // eslint-disable-next-line no-console
    console.warn(`Missing locale messages for ${locale}. Check if the locale file exists.`);
  }
  
  // Save previous locale to detect actual changes
  const previousLocale = i18n.locale;
  
  // Set the locale in i18n instance
  i18n.locale = locale;
  
  // Save in localStorage for persistence
  localStorage.setItem('user-locale', locale);
  
  // Update HTML lang attribute
  document.querySelector('html').setAttribute('lang', locale);
  
  // Clear translation cache when changing locale
  translationCache.clear();
  
  // Log the locale change for debugging
  // eslint-disable-next-line no-console
  console.log(`Language changed from ${previousLocale} to ${locale}`);
  
  // Emit an event when the language changes
  i18nEventBus.$emit('language-changed', locale);
  
  // Return whether the locale actually changed
  return previousLocale !== locale;
};

export default i18n;
